<template>
    <div
        ref="drawer"
        class="app-sidebar"
        :class="{ 'mobile-nav': isMobile, open: isMobile && mobileStore.isMobileNavOpen }"
        :role="isMobile ? 'dialog' : undefined"
        :aria-modal="isMobile ? 'true' : undefined"
        :aria-label="isMobile ? 'Main navigation' : undefined"
        :inert="isMobile && !mobileStore.isMobileNavOpen"
        tabindex="-1"
    >
        <!-- Mobile header -->
        <div v-if="isMobile" class="app-sidebar__mobile-header">
            <img
                v-if="isBusiness"
                :src="businessLogo"
                alt="Privacy Bee Business"
                class="app-sidebar__mobile-logo app-sidebar__logo-img--business"
                @click="navigateHome"
            >
            <SidebarLogo v-else class="app-sidebar__mobile-logo" @click="navigateHome" />
            <button class="app-sidebar__close" type="button" aria-label="Close navigation menu" @click="mobileStore.closeMobileNav">
                <PhX :size="24" />
            </button>
        </div>

        <div v-if="!isBusiness && authStore.loggedIn" class="px-4 mb-3 flex lg:hidden">
            <HeaderAccount class="flex w-full" />
        </div>

        <!-- Desktop logo -->
        <div class="app-sidebar__logo">
            <img
                v-if="isBusiness"
                :src="businessLogo"
                alt="Privacy Bee Business"
                class="app-sidebar__logo-img app-sidebar__logo-img--business"
                @click="navigateHome"
            >
            <SidebarLogo v-else class="app-sidebar__logo-img" @click="navigateHome" />
        </div>

        <!-- Top card (consumer variants only) -->
        <SidebarLicenseCard v-if="!isBusiness && authStore.loggedIn" class="mb-3" />

        <!-- Current company (business only): the one place the portal says which company is on screen. -->
        <div v-if="isBusiness && businessStore.companyName" class="app-sidebar__current-company">
            <SidebarBottomAvatar
                :name="businessStore.companyName"
                :image-src="businessStore.companyLogo"
            />
        </div>

        <!-- Nav -->
        <nav class="app-sidebar__nav" aria-label="Main">
            <!-- Consumer nav -->
            <template v-if="!isBusiness">
                <SidebarNavGroup>
                    <SidebarNavItem
                        label="Dashboard"
                        href="/dashboard"
                        :active="route.path === '/dashboard'"
                        :icon="PhGauge"
                    />
                    <SidebarNavItem
                        label="Identity Vault"
                        href="/vault"
                        :active="route.path === '/vault'"
                        :icon="PhVault"
                    />
                    <SidebarNavItem
                        label="Data Brokers"
                        href="/data-brokers"
                        :active="route.path.startsWith('/data-brokers')"
                        :icon="PhListMagnifyingGlass"
                    />
                    <SidebarNavItem
                        label="Search Presence"
                        href="/search-presence"
                        :active="route.path === '/search-presence'"
                        :icon="PhEye"
                        @click="searchPresence.visited = true"
                    >
                        <template v-if="!searchPresence.visited" #trailing>
                            <StatusIndicator class="!flex" variant="warning" label="New results" />
                        </template>
                    </SidebarNavItem>
                    <SidebarNavItem
                        label="Dark Web Presence"
                        href="/dark-web-scan"
                        :active="route.path === '/dark-web-scan'"
                        :icon="PhShieldWarning"
                    />
                    <SidebarNavItem
                        v-if="showsAccountSections"
                        label="Family Protection"
                        href="/family"
                        :active="route.path === '/family'"
                        :icon="PhAddressBook"
                    />
                    <SidebarNavItem
                        v-if="showsFreeLicenses"
                        label="Free licenses"
                        href="/free-licenses"
                        :active="route.path === '/free-licenses'"
                        :icon="PhGift"
                        :badge="licensesToGive || null"
                        :badge-label="licensesToGive ? `${licensesToGive} to give` : undefined"
                    />
                    <SidebarNavItem
                        v-if="showsAccountSections"
                        label="Reports"
                        href="/reports"
                        :active="route.path === '/reports'"
                        :icon="PhFiles"
                    />
                    <SidebarNavItem
                        label="Account settings"
                        href="/settings"
                        :active="route.path.startsWith('/settings')"
                        :icon="PhGearSix"
                    >
                        <template v-if="lpoaIndicator" #trailing>
                            <StatusIndicator class="!flex" variant="warning" label="Action required" />
                        </template>
                    </SidebarNavItem>
                </SidebarNavGroup>
            </template>

            <!-- Business nav -->
            <template v-else>
                <SidebarNavGroup>
                    <SidebarNavItem
                        v-if="hasDashboardAccess"
                        label="Dashboard"
                        :href="`/business/${slug}/dashboard`"
                        :active="businessRouteActive('/dashboard')"
                        :icon="PhGauge"
                    />
                    <SidebarNavItem
                        v-if="canViewEmployees"
                        label="Employees"
                        :href="`/business/${slug}/employees`"
                        :active="businessRouteActive('/employees')"
                        :icon="PhUsers"
                    />
                    <SidebarNavItem
                        v-if="canViewReporting"
                        label="Reporting"
                        :href="`/business/${slug}/reports`"
                        :active="businessRouteActive('/reports')"
                        :icon="PhChartBar"
                    />
                    <SidebarNavItem
                        v-if="canViewFinancial"
                        label="Billing & licenses"
                        :href="`/business/${slug}/financial`"
                        :active="businessRouteActive('/financial')"
                        :icon="PhCreditCard"
                    />
                    <SidebarNavItem
                        v-if="canViewApi"
                        label="Developer API"
                        :href="`/business/${slug}/api`"
                        :active="businessRouteActive('/api')"
                        :icon="PhKey"
                    />
                    <SidebarNavItem
                        v-if="canViewSettings"
                        label="Settings"
                        :href="settingsUrl"
                        :active="businessRouteActive('/settings')"
                        :icon="PhGearSix"
                    />
                </SidebarNavGroup>
            </template>
        </nav>

        <!-- Bottom -->
        <div class="app-sidebar__bottom">
            <template v-if="isBusiness">
                <SidebarBottomBusinessSwitcher />
            </template>
            <template v-else-if="hasBusinessAccount">
                <button
                    type="button"
                    class="app-sidebar__account-trigger"
                    @click="handleBusinessSwitchClick"
                >
                    <SidebarBottomAvatar
                        :name="employerName"
                        supporting="Business"
                        :initials-override="businessInitial"
                        :image-src="employer?.logo"
                    >
                        <template #trailing>
                            <PhCaretRight :size="16" />
                        </template>
                    </SidebarBottomAvatar>
                </button>
            </template>
            <template v-else>
                <a
                    href="https://business.privacybee.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="app-sidebar__account-trigger"
                >
                    <SidebarBottomAvatar
                        avatar-mode="icon"
                        name="Privacy Bee for Business"
                    >
                        <template #icon>
                            <LogoLock class="w-7 h-7" />
                        </template>
                        <template #trailing>
                            <PhArrowSquareOut :size="16" />
                        </template>
                    </SidebarBottomAvatar>
                </a>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
    PhAddressBook,
    PhArrowSquareOut,
    PhCaretRight,
    PhChartBar,
    PhCreditCard,
    PhEye,
    PhFiles,
    PhGauge,
    PhGearSix,
    PhGift,
    PhKey,
    PhListMagnifyingGlass,
    PhShieldWarning,
    PhUsers,
    PhVault,
    PhX,
} from '@phosphor-icons/vue';

import SidebarLogo from '~/assets/images/branding/sidebar-logo.svg';
import businessLogo from '~/assets/images/business/pb-logo-business-dark.png';
import LogoLock from '~/assets/images/branding/logo-lock.svg';

import { useAuthStore } from '~/stores/auth';
import { usePersonStore } from '~/stores/person';
import { useMobileStore } from '~/stores/layout/mobile';
import { useBusinessStore } from '~/stores/business';
import { useSearchPresenceStore } from '~/stores/person/search-presence';
import { useFreeLicensesStore } from '~/stores/person/free-licenses';

import HeaderAccount from '~/components/layout/Header/HeaderAccount.vue';
import StatusIndicator from '~/components/main/shared/StatusIndicator.vue';
import SidebarLicenseCard from './SidebarLicenseCard.vue';
import SidebarNavGroup from './SidebarNavGroup.vue';
import SidebarNavItem from './SidebarNavItem.vue';
import SidebarBottomAvatar from './SidebarBottomAvatar.vue';
import SidebarBottomBusinessSwitcher from './SidebarBottomBusinessSwitcher.vue';
import { prefersClassicBusinessUi } from '~/services/business-ui/preference';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const personStore = usePersonStore();
const mobileStore = useMobileStore();
const businessStore = useBusinessStore();

const { isMobile } = useIsMobile();

const drawer = ref<HTMLElement | null>(null);
const drawerIsOpen = computed(() => isMobile.value && mobileStore.isMobileNavOpen);

// HeaderAccount lives inside the drawer but its popover teleports to <body>, so
// the account actions sit outside the trapped subtree: without the teleported
// roots, Tab bounces off the popover's first action back to the drawer and
// billing, settings and account switching are unreachable.
useFocusTrap(drawer, drawerIsOpen, mobileStore.closeMobileNav, () =>
    Array.from(document.querySelectorAll<HTMLElement>('.p-popover')));

// Variant resolution
const isBusiness = computed(() => route.path.startsWith('/business/'));
const employer = computed(() => authStore.user?.employer ?? null);
const hasBusinessAccount = computed(() => Boolean(employer.value?.id));

const slug = computed(() => (route.params.slug as string) || employer.value?.slug || '');
const employerName = computed(() => employer.value?.name ?? '');
const businessInitial = computed(() => (employer.value?.name ?? '').charAt(0).toUpperCase());

const runtimeConfig = useRuntimeConfig();
const handleBusinessSwitchClick = () => {
    const e = employer.value;
    if (!e?.id) return;
    // The Hive business portal is staff-only; other accounts and classic-UI
    // opt-outs open the company portal in a separate tab.
    if (!authStore.user?.identity?.is_pb || prefersClassicBusinessUi()) {
        window.open(`${runtimeConfig.public.pb.appDomainLegacy}/business/${e.slug}/apps`, '_blank');
        return;
    }
    const target = `/business/${e.slug}/dashboard`;
    // Switching context must always end impersonation.
    if (personStore.impersonating) {
        personStore.endImpersonationTo(target);
        return;
    }
    router.push(target);
};

// Family Protection and Reports open on someone else's account only for Privacy Bee staff.
const showsAccountSections = computed(
    () => !personStore.impersonating || Boolean(authStore.user?.identity?.is_pb)
);

// Free licenses answer for the signed-in member, never for the person on screen, so the entry is
// read and shown only while the member is on their own account (stores/person/free-licenses.ts).
// Each page the member opens asks again until a read lands, so one that failed hides the entry for
// a page, not for the visit; once one is held, asking reads nothing.
const freeLicenses = useFreeLicensesStore();
const onOwnAccount = computed(() => !isBusiness.value && authStore.loggedIn && !personStore.impersonating);
const showsFreeLicenses = computed(() => onOwnAccount.value && freeLicenses.overview?.visible === true);
// A member whose plan earns none has none to give, whatever else the overview says.
const licensesToGive = computed(() => (freeLicenses.overview?.eligible ? freeLicenses.overview.credits : 0));
watch(
    [onOwnAccount, () => route.path],
    ([own]) => {
        if (own) void freeLicenses.ensureLoaded();
    },
    { immediate: true },
);

// Indicators
const searchPresence = useSearchPresenceStore();
const lpoaIndicator = computed(() => !personStore.person?.poa_signed);

// Business helpers
// Mirrors each destination page's own access gate so the sidebar never links
// to a page that immediately redirects the user away.
const hasDashboardAccess = computed(() => {
    if (authStore.user?.identity?.is_pb) return true;
    if (authStore.user?.employer?.slug === slug.value) return businessStore.hasPermission('dashboard');
    return businessStore.hasPermission('partner') || businessStore.hasPermission('businesses');
});
const canViewEmployees = computed(() => businessStore.hasPermission('employees', 'read'));
const canViewReporting = computed(() => businessStore.hasPermission('reporting'));
// The Financial Center lists and pays the company's invoices, those billed to its
// channel partner account included, so it is the one billing entry for whoever
// can read the company's finances.
const canViewFinancial = computed(() => businessStore.hasPermission('financial'));
// The entry shows for exactly the grant the Developer API page asks for, `api`:
// an API-only role is routed there by firstAccessibleDestination() and needs a
// nav item to get back by.
const canViewApi = computed(() => businessStore.hasPermission('api'));
const canViewBusinessSettings = computed(() => Boolean(slug.value) && businessStore.hasPermission('settings'));
const canViewRoleManagement = computed(
    () => Boolean(slug.value) && (businessStore.hasPermission('role-management') || businessStore.hasPermission('settings'))
);
// One Settings entry opens the tabbed settings shell; it's visible when the user
// can reach at least one settings tab (SettingsSubNav gates the individual tabs).
const canViewSettings = computed(() => canViewBusinessSettings.value || canViewRoleManagement.value);
// Land on General when permitted, otherwise the only tab a role-management-only
// user can reach (Roles & Permissions).
const settingsUrl = computed(() =>
    canViewBusinessSettings.value
        ? `/business/${slug.value}/settings`
        : `/business/${slug.value}/settings/roles-permissions`
);
const businessRouteActive = (path: string) => route.path.startsWith(`/business/${slug.value}${path}`);

// Navigation
const navigateHome = () => {
    if (isBusiness.value) {
        router.push(`/business/${slug.value}/dashboard`);
    } else {
        router.push({ path: '/dashboard', query: route.query });
    }
};

// Inline scroll locking is shared across layout chunks.
const { lockScroll, unlockScroll } = useScrollLock();

onUnmounted(() => {
    unlockScroll();
});

// Rotation can change the mobile breakpoint without changing the navigation flag.
// Lock scrolling only while navigation is a mobile drawer.
watch(
    [() => mobileStore.isMobileNavOpen, isMobile],
    ([open, mobile]) => {
        if (open && mobile) lockScroll();
        else unlockScroll();
    },
    { immediate: true },
);

watch(
    () => route.path,
    () => {
        if (isMobile.value) mobileStore.closeMobileNav();
    }
);
</script>

<style lang="scss" scoped>
.app-sidebar {
    @apply fixed w-[280px] h-full overflow-hidden bg-zinc-950 transition-transform duration-300 ease-in-out flex flex-col;
    z-index: var(--z-sticky);

    @media (max-width: 1023.98px) {
        width: 100%;
        transform: translateX(-100%);
    }

    &.mobile-nav {
        z-index: var(--z-drawer);
        transform: translateX(-100%);

        &.open {
            transform: translateX(0);
        }
    }

    &__mobile-header {
        @apply flex justify-between items-center p-4 shrink-0;

        .app-sidebar__mobile-logo {
            @apply w-44 cursor-pointer;
        }
    }

    &__close {
        @apply bg-transparent border-0 text-ink-invert cursor-pointer p-2 flex items-center justify-center;
    }

    &__logo {
        @apply h-[88px] lg:flex hidden items-center justify-center px-4 shrink-0;
    }

    &__logo-img {
        @apply mx-auto cursor-pointer;
    }

    // Business portal uses the "Privacy Bee business" wordmark, which carries a
    // descender line — constrain by width with auto height to keep it crisp.
    &__logo-img--business {
        @apply w-44 h-auto cursor-pointer;
    }

    &__current-company {
        @apply px-4 pb-3 mb-1 shrink-0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    &__nav {
        @apply flex-1 overflow-y-auto min-h-0;
    }

    &__bottom {
        @apply mt-auto p-4 shrink-0 w-full relative;
        background: linear-gradient(to left, rgba(245, 158, 11, 0) 0%, rgba(245, 158, 11, 0.05) 100%);

        &::before {
            content: '';
            @apply absolute top-0 left-0 right-0 pointer-events-none;
            height: 1px;
            background: linear-gradient(to right, rgba(245, 158, 11, 0.5) 0%, rgba(245, 158, 11, 0) 100%);
        }
    }

    &__account-trigger {
        @apply block w-full bg-transparent border-0 p-0 cursor-pointer no-underline;
    }
}
</style>
